import { useState, type FunctionComponent } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, Select, Space, Table, Tag, Typography, type TableProps } from "antd";
import { useNavigate } from "react-router-dom";

import { ChallengeStatus, type ChallengeEntity } from "@fitness-app/data-models/entities/Challenge";

import { supabase } from "~/store/initializeStore";
import { ChallengeFormDrawer } from "./ChallengeFormDrawer";
import { challengeStatusColors, challengeStatusLabels } from "./constants";
import { formatChallengeDateRange } from "./helpers";

type ChallengeListRow = ChallengeEntity & {
  participants: { count: number }[];
};

const pageSize = 25;

const statusFilterOptions = [
  { value: "all", label: "Wszystkie statusy" },
  ...Object.values(ChallengeStatus).map((status) => ({
    value: status,
    label: challengeStatusLabels[status],
  })),
];

const ChallengesListPage: FunctionComponent = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ChallengeStatus | "all">("all");
  const [isFormOpen, setIsFormOpen] = useState(false);

  const challengesQuery = useQuery({
    queryKey: ["challenges-list", page, statusFilter],
    keepPreviousData: true,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      let query = supabase
        .from("challenge")
        .select("*, participants:challenge_participant(count)", { count: "exact" })
        .order("startDate", { ascending: false })
        .range((page - 1) * pageSize, page * pageSize - 1);

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      const { data, error, count } = await query.returns<ChallengeListRow[]>();

      if (error) {
        throw error;
      }

      return {
        data: data ?? [],
        count: count ?? 0,
      };
    },
  });

  const handleChange: TableProps<ChallengeListRow>["onChange"] = (pagination) => {
    if (pagination.current && pagination.current !== page) {
      setPage(pagination.current);
    }
  };

  return (
    <Card
      title="Wyzwania"
      extra={
        <Space>
          <Select
            value={statusFilter}
            style={{ width: 200 }}
            options={statusFilterOptions}
            onChange={(value) => {
              setStatusFilter(value);
              setPage(1);
            }}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsFormOpen(true)}>
            Nowe wyzwanie
          </Button>
        </Space>
      }
    >
      <Table<ChallengeListRow>
        dataSource={challengesQuery.data?.data ?? []}
        rowKey="id"
        size="middle"
        scroll={{ x: true }}
        loading={challengesQuery.isFetching}
        locale={{ emptyText: "Brak wyzwań" }}
        onRow={(row) => ({
          onClick: () => navigate(`/challenges/${row.id}`),
          style: { cursor: "pointer" },
        })}
        pagination={{
          current: page,
          pageSize,
          total: challengesQuery.data?.count ?? 0,
          showSizeChanger: false,
        }}
        onChange={handleChange}
      >
        <Table.Column<ChallengeListRow>
          title="Nazwa"
          dataIndex="name"
          key="name"
          render={(name, row) => (
            <Space direction="vertical" size={0}>
              <Typography.Text strong>{name}</Typography.Text>
              {row.description ? (
                <Typography.Text type="secondary" ellipsis style={{ maxWidth: 320 }}>
                  {row.description}
                </Typography.Text>
              ) : null}
            </Space>
          )}
        />
        <Table.Column<ChallengeListRow>
          title="Status"
          dataIndex="status"
          key="status"
          render={(status: ChallengeStatus) => (
            <Tag color={challengeStatusColors[status] ?? "default"}>
              {challengeStatusLabels[status] ?? status}
            </Tag>
          )}
        />
        <Table.Column<ChallengeListRow>
          title="Okres"
          key="dateRange"
          render={(_, row) => formatChallengeDateRange(row.startDate, row.endDate)}
        />
        <Table.Column<ChallengeListRow>
          title="Uczestnicy"
          key="participants"
          width={120}
          render={(_, row) => row.participants?.[0]?.count ?? 0}
        />
        <Table.Column<ChallengeListRow>
          title="Kod dołączenia"
          dataIndex="joinCode"
          key="joinCode"
          render={(joinCode: string | null) =>
            joinCode ? (
              <Typography.Text copyable code>
                {joinCode}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">-</Typography.Text>
            )
          }
        />
        <Table.Column<ChallengeListRow>
          title="Opcje"
          key="action"
          render={(_, row) => (
            <Button
              type="link"
              className="px-0"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/challenges/${row.id}`);
              }}
            >
              Szczegóły
            </Button>
          )}
        />
      </Table>

      <ChallengeFormDrawer
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onCreated={() => {
          setIsFormOpen(false);
          void challengesQuery.refetch();
        }}
      />
    </Card>
  );
};

export default ChallengesListPage;
